import React, { useEffect, useState } from 'react';
import { Spin, Result } from 'antd';
import { getRazorpayOAuthUrl } from '../actions/user.action';
import { isValid } from '../utils/utilities';

/*
  Public page (no login required) that kicks off Razorpay OAuth consent for an operator.

  Usage: /razorpay-authorize?operatorCode=XXXX

  Behaviour:
    - reads operatorCode from the URL
    - asks the server whether a valid access token already exists
        - if yes  -> show "already authorized" (does NOT regenerate the token)
        - if no   -> redirect the browser to Razorpay's consent page; after the
                     operator approves, Razorpay redirects to the server callback
                     which stores the access/refresh tokens.
*/
export const RazorpayAuthorizePage = () => {
    const [status, setStatus] = useState('loading'); // loading | redirecting | already | error
    const [message, setMessage] = useState('');

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const operatorCode = params.get('operatorCode') || params.get('operator_code');

        if (!isValid(operatorCode)) {
            setStatus('error');
            setMessage('operatorCode is required in the URL, e.g. /razorpay-authorize?operatorCode=XXXX');
            return;
        }

        getRazorpayOAuthUrl(
            operatorCode,
            (responsePayload) => {
                if (isValid(responsePayload) && responsePayload.Status === 'SUCCESS' && isValid(responsePayload.Payload)) {
                    const payload = responsePayload.Payload;
                    if (payload.authorized) {
                        setStatus('already');
                    } else if (isValid(payload.url)) {
                        setStatus('redirecting');
                        window.location.href = payload.url;
                    } else {
                        setStatus('error');
                        setMessage('Could not obtain the Razorpay authorization URL.');
                    }
                } else {
                    setStatus('error');
                    setMessage(isValid(responsePayload) && isValid(responsePayload.Message)
                        ? responsePayload.Message
                        : 'Failed to start Razorpay authorization.');
                }
            },
            () => {
                setStatus('error');
                setMessage('Failed to reach the server. Please try again.');
            }
        );
    }, []);

    if (status === 'loading' || status === 'redirecting') {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: 16 }}>
                <Spin size="large" />
                <div>{status === 'redirecting' ? 'Redirecting to Razorpay…' : 'Checking Razorpay authorization…'}</div>
            </div>
        );
    }

    if (status === 'already') {
        return (
            <Result
                status="success"
                title="Razorpay already authorized"
                subTitle="This operator already has a valid Razorpay access token. No action needed."
            />
        );
    }

    return (
        <Result
            status="error"
            title="Razorpay authorization could not start"
            subTitle={message}
        />
    );
};
